import React, { useCallback, useEffect, useRef, useState } from "react";
import "./AdminValidationRules.css";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import SearchIcon from "@mui/icons-material/Search";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import StorefrontOutlinedIcon from "@mui/icons-material/StorefrontOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import useAdminHttp from "../../../hooks/use-admin-http";
import WLCustomModal from "../modals/WLCustomModal";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const SEVERITIES = [
  { value: "blocking", label: "Blocking" },
  { value: "flag", label: "Flag" },
  { value: "note", label: "Note" },
];

const CROSS_SEVERITIES = [
  { value: "blocking", label: "Blocking" },
  { value: "warning", label: "Warning" },
  { value: "info", label: "Info" },
];

// Applicant types. Sent as the "type" query parameter when fetching rules,
// document types and cross-document checks, and as the "type" field when a
// rule or cross-document check is created or edited.
const RULE_TYPES = [
  { value: "employed", label: "Employed", Icon: WorkOutlineIcon },
  {
    value: "self-employed",
    label: "Self-employed",
    Icon: StorefrontOutlinedIcon,
  },
  { value: "student", label: "Student", Icon: SchoolOutlinedIcon },
];

const TABS = [
  { key: "document", label: "Validation rules" },
  { key: "cross-document", label: "Cross-document checks" },
];

// Above this many document types the add rule modal switches to the wide
// layout with the document list in two columns.
const WIDE_DOCUMENT_THRESHOLD = 10;

const severityValue = (rule) => (rule.severity || "").toLowerCase();

const toggleInList = (list, value) =>
  list.includes(value)
    ? list.filter((item) => item !== value)
    : [...list, value];

// Ids of the document types a rule is linked to.
const ruleDocumentIds = (rule) =>
  (rule.application_documents || []).map((doc) => doc.id);

// A rule counts as active when it is active for at least one document type.
const isRuleActive = (rule) =>
  (rule.application_documents || []).some((doc) => doc.is_active);

const RuleModal = ({ documents, rule, ruleType, onClose, onSaved }) => {
  const [name, setName] = useState(rule ? rule.name : "");
  const [requirement, setRequirement] = useState(rule ? rule.requirement : "");
  const [severity, setSeverity] = useState(
    rule ? severityValue(rule) : "blocking",
  );
  // Editing starts from the document types the rule is linked to right now.
  const [documentIds, setDocumentIds] = useState(() =>
    rule ? ruleDocumentIds(rule) : [],
  );
  const { isLoading, error, sendRequest } = useAdminHttp();

  // The list only shows the document types of the active applicant type.
  // Links to document types of other applicant types are not shown, so they
  // are kept as they are rather than dropped on save.
  const hiddenIds = documentIds.filter(
    (id) => !documents.some((doc) => doc.id === id),
  );
  const allSelected =
    documents.length > 0 &&
    documents.every((doc) => documentIds.includes(doc.id));
  const canSave =
    name.trim() &&
    requirement.trim() &&
    documentIds.length + hiddenIds.length > 0;
  // A long catalog gets a wider modal with the document types in two columns.
  const isWide = documents.length > WIDE_DOCUMENT_THRESHOLD;

  const handleSave = () => {
    const body = {
      name: name.trim(),
      requirement: requirement.trim(),
      severity: severity.toUpperCase(),
      // Sent in catalog order, so the links are created in that order too.
      application_document_ids: [
        ...hiddenIds,
        ...documents
          .filter((doc) => documentIds.includes(doc.id))
          .map((doc) => doc.id),
      ],
    };

    // The applicant type travels in the body when editing, but as the
    // `type` query parameter when creating a rule.
    sendRequest(
      rule
        ? {
            url: `${BACKEND_URL}/welocate/validation-rules/${rule.id}`,
            method: "PATCH",
            body: { ...body, type: ruleType },
          }
        : {
            url: `${BACKEND_URL}/welocate/validation-rules?type=${ruleType}`,
            method: "POST",
            body: { ...body, is_active: true },
          },
      () => onSaved(),
    );
  };

  return (
    <WLCustomModal
      title={rule ? `Edit rule · ${rule.name}` : "Add rule"}
      width={isWide ? "1040px" : "820px"}
      visible={true}
      setVisible={(open) => {
        if (!open) onClose();
      }}
      btnText={isLoading ? "Saving..." : rule ? "Save changes" : "Save rule"}
      btnDisabled={!canSave || isLoading}
      btnOnClick={handleSave}
      secondaryBtnText="Cancel"
    >
      <div
        className={`wlv-form wlv-form--columns${
          isWide ? " wlv-form--wide" : ""
        }`}
      >
        <div className="wlv-form-column">
          <label className="wlv-form-field">
            <span className="wlv-form-label">Name</span>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Document is recent"
              autoFocus
            />
          </label>
          <label className="wlv-form-field">
            <span className="wlv-form-label">Requirement</span>
            <textarea
              value={requirement}
              onChange={(e) => setRequirement(e.target.value)}
              placeholder="Describe what the AI pre-scan should check for"
              rows={5}
            />
          </label>
          <label className="wlv-form-field">
            <span className="wlv-form-label">Severity</span>
            <div
              className={`wlv-severity-select wlv-severity-select--${severity} wlv-severity-select--full`}
            >
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                {SEVERITIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </label>
        </div>

        <div className="wlv-form-column">
          <div className="wlv-form-field">
            <div className="wlv-form-label-row">
              <span className="wlv-form-label">Applies to</span>
              {documents.length > 1 && (
                <button
                  type="button"
                  className="wlv-link-btn"
                  onClick={() =>
                    setDocumentIds(
                      allSelected ? [] : documents.map((doc) => doc.id),
                    )
                  }
                >
                  {allSelected ? "Clear selection" : "Select all"}
                </button>
              )}
            </div>
            <div
              className={`wlv-check-list${
                isWide ? " wlv-check-list--columns" : ""
              }`}
            >
              {documents.map((doc) => (
                <label key={doc.id} className="wlv-check-option">
                  <input
                    type="checkbox"
                    className="wlv-checkbox"
                    checked={documentIds.includes(doc.id)}
                    onChange={() =>
                      setDocumentIds((prev) => toggleInList(prev, doc.id))
                    }
                  />
                  <span>{doc.name}</span>
                </label>
              ))}
              {documents.length === 0 && (
                <span className="wlv-check-empty">No document types yet.</span>
              )}
            </div>
            {rule && documentIds.length > 1 && (
              <span className="wlv-check-hint">
                The rule is shared, so changes to its name, requirement and
                severity apply to every selected document type.
              </span>
            )}
          </div>
        </div>
        {error && (
          <div className="wlv-form-error wlv-form-error--span">{error}</div>
        )}
      </div>
    </WLCustomModal>
  );
};

const CrossCheckModal = ({
  documents,
  check,
  ruleType,
  onClose,
  onSaved,
}) => {
  const [title, setTitle] = useState(check ? check.title : "");
  const [description, setDescription] = useState(
    check ? check.description || "" : "",
  );
  const [documentIds, setDocumentIds] = useState(
    check ? (check.application_documents || []).map((doc) => doc.id) : [],
  );
  const [severity, setSeverity] = useState(
    check ? severityValue(check) : "blocking",
  );
  const { isLoading, error, sendRequest } = useAdminHttp();

  const canSave = title.trim() && documentIds.length > 0;

  const handleSave = () => {
    const body = {
      type: ruleType,
      title: title.trim(),
      description: description.trim(),
      severity: severity.toUpperCase(),
      application_document_ids: documentIds,
    };

    sendRequest(
      check
        ? {
            url: `${BACKEND_URL}/welocate/cross-document-checks/${check.id}`,
            method: "PUT",
            body,
          }
        : {
            url: `${BACKEND_URL}/welocate/cross-document-checks`,
            method: "POST",
            body,
          },
      () => onSaved(),
    );
  };

  return (
    <WLCustomModal
      title={check ? "Edit cross-document check" : "Add cross-document check"}
      width="1040px"
      visible={true}
      setVisible={(open) => {
        if (!open) onClose();
      }}
      btnText={isLoading ? "Saving..." : check ? "Save changes" : "Save check"}
      btnDisabled={!canSave || isLoading}
      btnOnClick={handleSave}
      secondaryBtnText="Cancel"
    >
      <div className="wlv-form wlv-form--columns wlv-form--wide">
        <div className="wlv-form-column">
          <label className="wlv-form-field">
            <span className="wlv-form-label">Title</span>
            <textarea
              className="wlv-form-title"
              value={title}
              onChange={(e) => setTitle(e.target.value.replace(/\n/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") e.preventDefault();
              }}
              placeholder="e.g. Income matches bank statements"
              rows={2}
              autoFocus
            />
          </label>
          <label className="wlv-form-field">
            <span className="wlv-form-label">Description</span>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what should be compared across the documents"
              rows={9}
            />
          </label>
          <label className="wlv-form-field">
            <span className="wlv-form-label">Severity</span>
            <div
              className={`wlv-severity-select wlv-severity-select--${severity} wlv-severity-select--full`}
            >
              <select
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
              >
                {CROSS_SEVERITIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </label>
        </div>

        <div className="wlv-form-column">
          <div className="wlv-form-field">
            <span className="wlv-form-label">
              Documents this check applies to
            </span>
            <div className="wlv-check-list wlv-check-list--columns">
              {documents.map((doc) => (
                <label key={doc.id} className="wlv-check-option">
                  <input
                    type="checkbox"
                    className="wlv-checkbox"
                    checked={documentIds.includes(doc.id)}
                    onChange={() =>
                      setDocumentIds((prev) => toggleInList(prev, doc.id))
                    }
                  />
                  <span>{doc.name}</span>
                </label>
              ))}
              {documents.length === 0 && (
                <span className="wlv-check-empty">No document types yet.</span>
              )}
            </div>
          </div>
        </div>
        {error && (
          <div className="wlv-form-error wlv-form-error--span">{error}</div>
        )}
      </div>
    </WLCustomModal>
  );
};

// Fixed-position popover anchored to a trigger. It closes on an outside
// click, Escape, scrolling the page or resizing. Fixed coordinates keep it
// clear of the section cards, which hide their overflow for the rounded
// corners.
const usePopover = (align = "right") => {
  const [position, setPosition] = useState(null); // null while closed
  const triggerRef = useRef(null);
  const menuRef = useRef(null);
  const isOpen = position !== null;

  const open = () => {
    const rect = triggerRef.current.getBoundingClientRect();
    setPosition(
      align === "left"
        ? { top: rect.bottom + 6, left: rect.left }
        : { top: rect.bottom + 6, right: window.innerWidth - rect.right },
    );
  };
  const close = () => setPosition(null);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (event) => {
      if (
        !menuRef.current?.contains(event.target) &&
        !triggerRef.current?.contains(event.target)
      )
        close();
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") close();
    };
    // Scrolling inside the popover itself (a long checklist) keeps it open.
    const onScroll = (event) => {
      if (!menuRef.current?.contains(event.target)) close();
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  return {
    isOpen,
    position,
    triggerRef,
    menuRef,
    close,
    toggle: () => (isOpen ? close() : open()),
  };
};

// Three dot button that opens a small action menu.
const RowMenu = ({ label, items }) => {
  const { isOpen, position, triggerRef, menuRef, close, toggle } =
    usePopover("right");

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`wlv-menu-trigger${isOpen ? " wlv-menu-trigger--open" : ""}`}
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        onClick={toggle}
      >
        <MoreHorizIcon />
      </button>
      {isOpen && (
        <div ref={menuRef} className="wlv-menu" role="menu" style={position}>
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              role="menuitem"
              className={`wlv-menu-item${
                item.danger ? " wlv-menu-item--danger" : ""
              }`}
              onClick={() => {
                close();
                item.onClick();
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </div>
      )}
    </>
  );
};

// Toolbar filter on one or more document types: a trigger styled like the
// other toolbar controls that opens a checklist.
const DocumentFilter = ({ documents, selectedIds, onChange }) => {
  const { isOpen, position, triggerRef, menuRef, toggle } = usePopover("left");

  const label =
    selectedIds.length === 0
      ? "All document types"
      : selectedIds.length === 1
        ? documents.find((doc) => doc.id === selectedIds[0])?.name ||
          "1 document type"
        : `${selectedIds.length} document types`;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={`wlv-filter-trigger${
          isOpen || selectedIds.length > 0 ? " wlv-filter-trigger--active" : ""
        }`}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Filter by document type"
        onClick={toggle}
      >
        <DescriptionOutlinedIcon />
        <span className="wlv-filter-trigger-label">{label}</span>
      </button>
      {isOpen && (
        <div
          ref={menuRef}
          className="wlv-menu wlv-filter-menu"
          style={position}
        >
          <div className="wlv-filter-menu-header">
            <span className="wlv-form-label">Document types</span>
            {selectedIds.length > 0 && (
              <button
                type="button"
                className="wlv-link-btn"
                onClick={() => onChange([])}
              >
                Clear
              </button>
            )}
          </div>
          <div className="wlv-check-list">
            {documents.map((doc) => (
              <label key={doc.id} className="wlv-check-option">
                <input
                  type="checkbox"
                  className="wlv-checkbox"
                  checked={selectedIds.includes(doc.id)}
                  onChange={() => onChange(toggleInList(selectedIds, doc.id))}
                />
                <span>{doc.name}</span>
              </label>
            ))}
            {documents.length === 0 && (
              <span className="wlv-check-empty">No document types yet.</span>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const DeleteModal = ({ label, name, note, url, onClose, onDeleted }) => {
  const { isLoading, error, sendRequest } = useAdminHttp();

  const handleDelete = () => {
    sendRequest({ url, method: "DELETE" }, () => onDeleted());
  };

  return (
    <WLCustomModal
      title={`Delete ${label}`}
      visible={true}
      setVisible={(open) => {
        if (!open) onClose();
      }}
      btnText={isLoading ? "Deleting..." : `Delete ${label}`}
      btnDisabled={isLoading}
      btnOnClick={handleDelete}
      secondaryBtnText="Cancel"
    >
      <div className="wlv-form">
        <p className="wlv-delete-text">
          Are you sure you want to delete <strong>{name}</strong>? This cannot
          be undone.
        </p>
        {note && <p className="wlv-delete-text">{note}</p>}
        {error && <div className="wlv-form-error">{error}</div>}
      </div>
    </WLCustomModal>
  );
};

const AdminValidationRules = () => {
  const [activeTab, setActiveTab] = useState("document");
  const [ruleType, setRuleType] = useState(RULE_TYPES[0].value);
  const [documents, setDocuments] = useState([]);
  const [rules, setRules] = useState([]);
  const [ruleModal, setRuleModal] = useState(null); // { rule? }
  const [deleteRule, setDeleteRule] = useState(null);
  const [crossChecks, setCrossChecks] = useState([]);
  const [crossCheckModal, setCrossCheckModal] = useState(null); // { check? }
  const [deleteCrossCheck, setDeleteCrossCheck] = useState(null);
  const [ruleSearch, setRuleSearch] = useState("");
  const [ruleSeverity, setRuleSeverity] = useState("all");
  const [ruleDocumentFilter, setRuleDocumentFilter] = useState([]);
  const [crossSearch, setCrossSearch] = useState("");
  const [crossSeverity, setCrossSeverity] = useState("all");
  const {
    isLoading: isLoadingDocuments,
    error: documentsError,
    sendRequest: sendDocumentsRequest,
  } = useAdminHttp();
  const {
    isLoading: isLoadingRules,
    error: rulesError,
    sendRequest: sendRulesRequest,
  } = useAdminHttp();
  const { error: toggleError, sendRequest: sendToggleRequest } = useAdminHttp();
  const {
    isLoading: isLoadingCrossChecks,
    error: crossChecksError,
    sendRequest: sendCrossChecksRequest,
  } = useAdminHttp();

  const fetchCrossChecks = useCallback(() => {
    sendCrossChecksRequest(
      {
        url: `${BACKEND_URL}/welocate/cross-document-checks?type=${ruleType}`,
      },
      (data) => setCrossChecks(data || []),
    );
  }, [sendCrossChecksRequest, ruleType]);

  const fetchDocuments = useCallback(() => {
    sendDocumentsRequest(
      {
        url: `${BACKEND_URL}/welocate/application-documents?type=${ruleType}`,
      },
      (data) => setDocuments(data || []),
    );
  }, [sendDocumentsRequest, ruleType]);

  // Rules linked to at least one document type of the active applicant
  // type, narrowed further to the chosen document types when filtered.
  const fetchRules = useCallback(() => {
    sendRulesRequest(
      {
        url: `${BACKEND_URL}/welocate/validation-rules/search`,
        method: "POST",
        body: {
          type: ruleType,
          application_document_ids:
            ruleDocumentFilter.length > 0 ? ruleDocumentFilter : null,
        },
      },
      (data) => setRules(data || []),
    );
  }, [sendRulesRequest, ruleType, ruleDocumentFilter]);

  useEffect(() => {
    fetchDocuments();
    fetchCrossChecks();
  }, [fetchDocuments, fetchCrossChecks]);

  useEffect(() => {
    fetchRules();
  }, [fetchRules]);

  // A failed toggle leaves the optimistic flip on screen; resync with the
  // server so the switches reflect the actual stored state again.
  useEffect(() => {
    if (toggleError) fetchRules();
  }, [toggleError, fetchRules]);

  // The document filter holds ids of the previous applicant type, so it is
  // cleared along with the switch.
  const selectRuleType = (value) => {
    setRuleType(value);
    setRuleDocumentFilter([]);
  };

  const isLoading = isLoadingDocuments || isLoadingRules;
  const error = documentsError || rulesError;
  const totalActive = rules.filter(isRuleActive).length;

  const isRuleFiltered =
    ruleSearch.trim() !== "" ||
    ruleSeverity !== "all" ||
    ruleDocumentFilter.length > 0;
  const filteredRules = rules.filter((rule) => {
    const matchesSearch = (rule.name || "")
      .toLowerCase()
      .includes(ruleSearch.trim().toLowerCase());
    const matchesSeverity =
      ruleSeverity === "all" || severityValue(rule) === ruleSeverity;
    return matchesSearch && matchesSeverity;
  });

  const filteredCrossChecks = crossChecks.filter((check) => {
    const matchesSearch = (check.title || "")
      .toLowerCase()
      .includes(crossSearch.trim().toLowerCase());
    const matchesSeverity =
      crossSeverity === "all" || severityValue(check) === crossSeverity;
    return matchesSearch && matchesSeverity;
  });
  const isCrossFiltered = crossSearch.trim() !== "" || crossSeverity !== "all";

  const tabCounts = {
    document: rules.length,
    "cross-document": crossChecks.length,
  };

  const updateRuleLinks = (ruleId, patchLink) =>
    setRules((prev) =>
      prev.map((rule) =>
        rule.id === ruleId
          ? {
              ...rule,
              application_documents: (rule.application_documents || []).map(
                patchLink,
              ),
            }
          : rule,
      ),
    );

  // Flips the rule for every document type it is linked to.
  const toggleRuleActive = (rule) => {
    const is_active = !isRuleActive(rule);
    updateRuleLinks(rule.id, (doc) => ({ ...doc, is_active }));
    sendToggleRequest(
      {
        url: `${BACKEND_URL}/welocate/validation-rules/${rule.id}`,
        method: "PATCH",
        body: { is_active },
      },
      () => {},
    );
  };

  // Flips the rule for one document type only.
  const toggleRuleDocument = (rule, doc) => {
    const is_active = !doc.is_active;
    updateRuleLinks(rule.id, (link) =>
      link.id === doc.id ? { ...link, is_active } : link,
    );
    sendToggleRequest(
      {
        url: `${BACKEND_URL}/welocate/validation-rules/${rule.id}`,
        method: "PATCH",
        body: { is_active, application_document_id: doc.id },
      },
      () => {},
    );
  };

  // A rule can also be linked to document types of another applicant type;
  // those chips carry that type so same-named documents can be told apart.
  const documentChipLabel = (doc) => {
    const type = RULE_TYPES.find((option) => option.value === doc.type);
    return doc.type && doc.type !== ruleType && type
      ? `${doc.name} · ${type.label}`
      : doc.name;
  };

  return (
    <div className="wlv-page">
      <div className="wlv-page-header">
        <div>
          <h1 className="wla-page-title">Screening checks</h1>
          <p className="wla-page-subtitle">
            {totalActive} active rules across {documents.length} document types
            · applied by the AI pre-scan when an applicant uploads a document
          </p>
        </div>
        <div
          className="wlv-type-toggle"
          role="group"
          aria-label="Applicant type"
        >
          {RULE_TYPES.map((type) => (
            <button
              key={type.value}
              type="button"
              className={`wlv-type-toggle-btn${
                ruleType === type.value ? " wlv-type-toggle-btn--active" : ""
              }`}
              aria-pressed={ruleType === type.value}
              onClick={() => selectRuleType(type.value)}
            >
              <type.Icon />
              {type.label}
            </button>
          ))}
        </div>
      </div>

      <div className="wlv-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`wlv-tab${
              activeTab === tab.key ? " wlv-tab--active" : ""
            }`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
            <span className="wlv-tab-count">{tabCounts[tab.key]}</span>
          </button>
        ))}
      </div>

      {activeTab === "document" && (
        <>
          <div className="wlv-toolbar">
            <div className="wlv-search">
              <SearchIcon className="wlv-search-icon" />
              <input
                type="text"
                value={ruleSearch}
                onChange={(e) => setRuleSearch(e.target.value)}
                placeholder="Search by rule name"
              />
            </div>
            <div
              className={`wlv-severity-select wlv-severity-select--${ruleSeverity}`}
            >
              <select
                value={ruleSeverity}
                onChange={(e) => setRuleSeverity(e.target.value)}
                aria-label="Filter by severity"
              >
                <option value="all">All severities</option>
                {SEVERITIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <DocumentFilter
              documents={documents}
              selectedIds={ruleDocumentFilter}
              onChange={setRuleDocumentFilter}
            />
            <button className="wlv-add-btn" onClick={() => setRuleModal({})}>
              <AddIcon /> Add rule
            </button>
          </div>

          {toggleError && (
            <div className="wlv-form-error wlv-page-error">
              Could not update the rule: {toggleError}
            </div>
          )}

          {isLoading && (
            <div className="wlv-empty">Loading validation rules...</div>
          )}
          {!isLoading && error && <div className="wlv-empty">{error}</div>}
          {!isLoading && !error && filteredRules.length === 0 && (
            <div className="wlv-empty">
              {documents.length === 0
                ? "No document types yet."
                : isRuleFiltered
                  ? "No rules match your filters."
                  : "No rules yet."}
            </div>
          )}

          {!isLoading &&
            !error &&
            filteredRules.map((rule) => {
              const active = isRuleActive(rule);
              return (
                <div
                  key={rule.id}
                  className={`wlv-section wlv-rule-card${
                    active ? "" : " wlv-rule-card--disabled"
                  }`}
                >
                  <div className="wlv-rule-card-header">
                    <button
                      className={`wlv-toggle${active ? " wlv-toggle--on" : ""}`}
                      role="switch"
                      aria-checked={active}
                      aria-label={`Toggle ${rule.name} for all document types`}
                      title={
                        active
                          ? "Deactivate for all document types"
                          : "Activate for all document types"
                      }
                      onClick={() => toggleRuleActive(rule)}
                    >
                      <span className="wlv-toggle-knob" />
                    </button>
                    <div className="wlv-rule-text">
                      <div className="wlv-rule-title">{rule.name}</div>
                      <div className="wlv-rule-description">
                        {rule.requirement}
                      </div>
                    </div>
                    <span
                      className={`wlv-badge wlv-badge--${severityValue(rule)}`}
                    >
                      {SEVERITIES.find(
                        (severity) => severity.value === severityValue(rule),
                      )?.label || rule.severity}
                    </span>
                    <RowMenu
                      label={`Actions for ${rule.name}`}
                      items={[
                        {
                          label: "Edit",
                          icon: <EditOutlinedIcon />,
                          onClick: () => setRuleModal({ rule }),
                        },
                        {
                          label: "Delete",
                          icon: <DeleteOutlineIcon />,
                          danger: true,
                          onClick: () => setDeleteRule(rule),
                        },
                      ]}
                    />
                  </div>

                  <div className="wlv-rule-card-meta">
                    <span className="wlv-cross-check-meta-label">
                      Applies to
                    </span>
                    <div className="wlv-chips">
                      {(rule.application_documents || []).map((doc) => (
                        <button
                          key={doc.id}
                          type="button"
                          className={`wlv-chip wlv-chip--toggle${
                            doc.is_active ? " wlv-chip--on" : " wlv-chip--off"
                          }`}
                          aria-pressed={doc.is_active}
                          title={
                            doc.is_active
                              ? `Active for ${doc.name}. Click to deactivate.`
                              : `Inactive for ${doc.name}. Click to activate.`
                          }
                          onClick={() => toggleRuleDocument(rule, doc)}
                        >
                          {doc.is_active ? <CheckIcon /> : <CloseIcon />}
                          {documentChipLabel(doc)}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              );
            })}
        </>
      )}

      {activeTab === "cross-document" && (
        <>
          <div className="wlv-toolbar">
            <div className="wlv-search">
              <SearchIcon className="wlv-search-icon" />
              <input
                type="text"
                value={crossSearch}
                onChange={(e) => setCrossSearch(e.target.value)}
                placeholder="Search by title"
              />
            </div>
            <div
              className={`wlv-severity-select wlv-severity-select--${crossSeverity}`}
            >
              <select
                value={crossSeverity}
                onChange={(e) => setCrossSeverity(e.target.value)}
                aria-label="Filter by severity"
              >
                <option value="all">All severities</option>
                {CROSS_SEVERITIES.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
            <button
              className="wlv-add-btn"
              onClick={() => setCrossCheckModal({})}
            >
              <AddIcon /> Add cross-document check
            </button>
          </div>

          {isLoadingCrossChecks && (
            <div className="wlv-empty">Loading cross-document checks...</div>
          )}
          {!isLoadingCrossChecks && crossChecksError && (
            <div className="wlv-empty">{crossChecksError}</div>
          )}
          {!isLoadingCrossChecks &&
            !crossChecksError &&
            filteredCrossChecks.length === 0 && (
              <div className="wlv-empty">
                {isCrossFiltered
                  ? "No cross-document checks match your filters."
                  : "No cross-document checks yet."}
              </div>
            )}
          {!isLoadingCrossChecks &&
            !crossChecksError &&
            filteredCrossChecks.map((check) => (
              <div
                key={check.id}
                className={`wlv-section wlv-cross-check${
                  check.is_active ? "" : " wlv-cross-check--disabled"
                }`}
              >
                <div className="wlv-cross-check-header">
                  <div className="wlv-cross-check-text">
                    <div className="wlv-rule-title">{check.title}</div>
                    {check.description && (
                      <div className="wlv-rule-description">
                        {check.description}
                      </div>
                    )}
                  </div>
                  <span
                    className={`wlv-badge wlv-badge--${severityValue(check)}`}
                  >
                    {CROSS_SEVERITIES.find(
                      (severity) => severity.value === severityValue(check),
                    )?.label || check.severity}
                  </span>
                  <button
                    className="wlv-edit-btn"
                    onClick={() => setCrossCheckModal({ check })}
                  >
                    <EditOutlinedIcon /> Edit
                  </button>
                  <button
                    className="wlv-icon-btn"
                    aria-label="Delete cross-document check"
                    onClick={() => setDeleteCrossCheck(check)}
                  >
                    <DeleteOutlineIcon />
                  </button>
                </div>

                <div className="wlv-cross-check-meta">
                  <div className="wlv-cross-check-meta-group">
                    <span className="wlv-cross-check-meta-label">
                      Documents
                    </span>
                    <div className="wlv-chips">
                      {(check.application_documents || []).map((doc) => (
                        <span
                          key={doc.id}
                          className="wlv-chip wlv-chip--document"
                        >
                          <DescriptionOutlinedIcon />
                          {doc.name}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
        </>
      )}

      {crossCheckModal && (
        <CrossCheckModal
          documents={documents}
          check={crossCheckModal.check}
          ruleType={ruleType}
          onClose={() => setCrossCheckModal(null)}
          onSaved={() => {
            setCrossCheckModal(null);
            fetchCrossChecks();
          }}
        />
      )}

      {ruleModal && (
        <RuleModal
          documents={documents}
          rule={ruleModal.rule}
          ruleType={ruleType}
          onClose={() => setRuleModal(null)}
          onSaved={() => {
            setRuleModal(null);
            fetchRules();
          }}
        />
      )}

      {deleteRule && (
        <DeleteModal
          label="rule"
          name={deleteRule.name}
          note={
            ruleDocumentIds(deleteRule).length > 1
              ? `This rule applies to ${
                  ruleDocumentIds(deleteRule).length
                } document types and will be removed from all of them.`
              : null
          }
          url={`${BACKEND_URL}/welocate/validation-rules/${deleteRule.id}`}
          onClose={() => setDeleteRule(null)}
          onDeleted={() => {
            setDeleteRule(null);
            fetchRules();
          }}
        />
      )}

      {deleteCrossCheck && (
        <DeleteModal
          label="check"
          name={deleteCrossCheck.title}
          url={`${BACKEND_URL}/welocate/cross-document-checks/${deleteCrossCheck.id}`}
          onClose={() => setDeleteCrossCheck(null)}
          onDeleted={() => {
            setDeleteCrossCheck(null);
            fetchCrossChecks();
          }}
        />
      )}
    </div>
  );
};

export default AdminValidationRules;
